import Handsontable from "handsontable";
import Choices from "choices.js";
import {
  path as Rpath,
  pluck as Rpluck,
  prop as Rprop,
  isEmpty as RisEmpty,
  isNil as RisNil,
  map as Rmap,
  assoc as Rassoc,
  defaultTo as RdefaultTo,
} from "ramda";
import { v4 as uuidv4 } from "uuid";
import store from "../stores/main";

import { apolloClient } from "@/services/vue-apollo";
import { uniqBy } from "lodash";
import { defaultVariables } from "./relationship-sections";
import { scrubOffUnnecessaryLettersFromText } from "./misc";

const { TextEditor } = Handsontable.editors;
const { addClass, removeClass, hasClass } = Handsontable.dom;
const { text: TextCellType } = Handsontable.cellTypes;
const { KEY_CODES, rangeEach } = Handsontable.helper;

let choicesSearchSetTimeout = null;

let setDataTimeout = null;

const EDITOR_HIDDEN_CLASS_NAME = "ht_editor_hidden";
const EDITOR_VISIBLE_CLASS_NAME = "ht_editor_visible";

/**
 * set search options in store so we can reference the items later
 */

const setDataReference = (query, data) => {
  clearTimeout(setDataTimeout);
  if (data.length === 0) return;
  setDataTimeout = setTimeout(() => {
    store.dispatch("actiongrid/setGridColumnDataReference", {
      key: query,
      data,
    });
  }, 1000);
};

/**
 * every option that has ever been passed to choices would be cached
 * so we can always set choices with old searches and items previously searched
 * */
let cachedSearchedAndQuickAddOptions = {};

const handleCachedSearchedAndQuickAddOptions = (options, queryKey) => {
  cachedSearchedAndQuickAddOptions = {
    ...cachedSearchedAndQuickAddOptions,
    [queryKey]: [
      ...(cachedSearchedAndQuickAddOptions[queryKey] ?? []),
      ...options,
    ],
  };
};

const getCachedSearchedAndQuickAddOptions = (options, queryKey) => {
  const items = uniqBy(
    [...options, ...(cachedSearchedAndQuickAddOptions[queryKey] ?? [])],
    "id"
  );
  return items;
};

const classNamesOverride = {
  containerOuter: "multi-select__choices",
  containerInner: "multi-select__choices__inner",
  input: "multi-select__choices__input",
  inputCloned: "multi-select__choices__input--cloned",
  list: "multi-select__choices__list",
  listItems: "multi-select__choices__list--multiple",
  listSingle: "multi-select__choices__list--single",
  listDropdown: "multi-select__choices__list--dropdown",
  item: "multi-select__choices__item",
  itemSelectable: "multi-select__choices__item--selectable",
  itemDisabled: "multi-select__choices__item--disabled",
  itemChoice: "multi-select__choices__item--choice",
  placeholder: "multi-select__choices__placeholder",
  group: "multi-select__choices__group",
  groupHeading: "multi-select__choices__heading",
  button: "multi-select__choices__button",
};

const getSeparator = Rpath(["config", "separator"]);
const getAddItem = Rpath(["config", "quickAdd"]);
const getQueryDocument = Rpath(["config", "doc"]);
const getQueryKey = Rpath(["config", "query"]);
const getVariables = Rpath(["config", "getVariables"]);
const getLabelKey = Rpath(["config", "labelKey"]);
const getValueKey = Rpath(["config", "valueKey"]);
const getMaxItemCount = Rpath(["config", "maxItemCount"]);

function numberComparator(a, b) {
  return a.value - b.value;
}

const isNumber = (value) => typeof value === "number" && isFinite(value);

export class MultiSelectEditor extends TextEditor {
  /**
   * Gets current value from editable element.
   *
   * @returns {String}
   */
  getValue() {
    const valueArray = this.choices.getValue();
    const formattedValues = Rpluck("label", valueArray);
    return formattedValues.join(this.separator);
  }

  getFinalValue() {
    this.valueKey = getValueKey(this.selectOptions) || "value";
    this.labelKey = getLabelKey(this.selectOptions) || "label";
    return this.choices
      .getValue()
      .map((v) => ({ [this.valueKey]: v.value, [this.labelKey]: v.label }));
  }

  /**
   * Prepares editor's meta data.
   *
   * @param {Number} row
   * @param {Number} col
   * @param {Number|String} prop
   * @param {HTMLTableCellElement} td
   * @param {*} originalValue
   * @param {Object} cellProperties
   */
  prepare(row, col, prop, td, originalValue, cellProperties) {
    super.prepare(row, col, prop, td, originalValue, cellProperties);
    const { type } = cellProperties;
    this.type = type;

    const selectOptions = Rprop("select", cellProperties);
    this.selectOptions = RdefaultTo({}, selectOptions);

    this.valueKey = getValueKey(this.selectOptions) || "value";
    this.labelKey = getLabelKey(this.selectOptions) || "label";
    this.addItem = getAddItem(this.selectOptions) || false;
    this.queryDocument = getQueryDocument(this.selectOptions) || null;
    this.queryKey = getQueryKey(this.selectOptions) || "query";
    this.getVariables = getVariables(this.selectOptions) ?? defaultVariables;

    if (type === "numeric") {
      this.separator = "|";
      this.maxItemCount = 1;
      this.originalValue = isNumber(originalValue)
        ? originalValue.toString()
        : originalValue;
    } else {
      this.separator = getSeparator(this.selectOptions) || ",";
      this.maxItemCount = getMaxItemCount(this.selectOptions) || -1;
    }
  }

  /**
   * Creates an editor's elements and adds necessary CSS classnames.
   */
  createElements() {
    this.TEXTAREA = document.createElement("select");
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    this.TEXTAREA.select = () => {};
    this.TEXTAREA.tabIndex = -1;
    addClass(this.TEXTAREA, "handsontableInput");
    this.TEXTAREA.setAttribute("multiple", true);

    this.TEXTAREA_PARENT = document.createElement("div");
    addClass(this.TEXTAREA_PARENT, "handsontableInputHolder");

    if (hasClass(this.TEXTAREA_PARENT, this.layerClass)) {
      removeClass(this.TEXTAREA_PARENT, this.layerClass);
    }

    addClass(this.TEXTAREA_PARENT, EDITOR_HIDDEN_CLASS_NAME);

    this.textareaStyle = this.TEXTAREA.style;
    this.textareaStyle.width = 0;
    this.textareaStyle.height = 0;
    this.textareaStyle.overflowY = "visible";

    this.textareaParentStyle = this.TEXTAREA_PARENT.style;

    this.TEXTAREA_PARENT.appendChild(this.TEXTAREA);
    this.instance.rootElement.appendChild(this.TEXTAREA_PARENT);
  }

  /**
   * Opens the editor and adjust its size.
   */
  open(event) {
    this.refreshDimensions();

    this.instance.addHook("beforeKeyDown", (event) =>
      this.onBeforeKeyDown(event)
    );

    if (this.choices) this.choices.destroy();

    const choicesOptions = {
      classNames: classNamesOverride,
      delimiter: this.separator,
      allowHTML: true,
      duplicateItemsAllowed: false,
      removeItemButton: true,
      position: "auto",
      // itemSelectText: "",
      loadingText: `Loading ${this.queryKey}...`,
      // addItems: true,
      // addItemText: (value) => {
      //   return `Press Enter to add <b>"${value}"</b>`;
      // },
      noResultsText: this.addItem
        ? `Press Enter to add`
        : `No ${this.queryKey} found`,
      maxItemCount: this.maxItemCount,
      noChoicesText: `No ${this.queryKey} to choose from`,
      fuseOptions: {
        include: "score",
        threshold: 0.1,
      },
    };

    if (this.type === "numeric") {
      Object.assign(choicesOptions, {
        sorter: numberComparator,
      });
    }

    this.choices = new Choices(this.TEXTAREA, choicesOptions);

    const getOptions = () => {
      const { options } = this.selectOptions;
      /**
       * on opened the 'options' will always be the default passed to the instance
       * so we need to add search options and quick add options to it so these options can be reference properly
       */
      // options = getCachedSearchedAndQuickAddOptions(options, this.queryKey);
      const toResolve = typeof options === "function" ? options() : options;
      return Promise.resolve(toResolve).then((availableOptions) => {
        const selectedItems = [];
        const { originalValue } = this;

        if (originalValue) {
          originalValue.map((original) => {
            if (!availableOptions.find((option) => option.id === original.id)) {
              selectedItems.push(original);
            }
          });
          availableOptions = availableOptions.concat(selectedItems);
        }

        if (RisEmpty(originalValue) || RisNil(originalValue)) {
          return availableOptions;
        }
        // const selectedValues = originalValue.split(this.separator)
        return Rmap((item) => {
          const label = `${Rprop(this.labelKey, item)}`;

          return originalValue.find((v) => v[this.labelKey] === label)
            ? Rassoc("selected", true, item)
            : item;
        }, availableOptions);
      });
    };

    this.choices.setChoices(getOptions, this.valueKey, this.labelKey, true);

    this.choices.passedElement.element.addEventListener(
      "change",
      this.onChoicesChange.bind(this)
    );

    this.choices.showDropdown();

    this.TEXTAREA.addEventListener("hideDropdown", this.close.bind(this));

    // Workaround to add value if more than 31 characters
    this.TEXTAREA_PARENT.addEventListener(
      "keydown",
      this.onBeforeKeyDownOnSelect.bind(this),
      true
    );

    this.TEXTAREA_PARENT.querySelector("input").addEventListener(
      "keydown",
      this.onBeforeKeyDownOnInput.bind(this)
    );

    if (event.key && !["Enter"].includes(event.key)) {
      this.choices.input.value = event.key;
    }
  }

  /**
   * Closes the editor.
   */
  close() {
    this.choices?.hideDropdown();
    this.autoResize.unObserve();
    this.hideEditableElement();
    this.clearHooks();
  }

  focus() {
    this.instance.listen();
  }

  saveValue() {
    const { visualRow, visualCol } = this.cellProperties;
    this.instance.setDataAtCell(
      visualRow,
      visualCol,
      this.getFinalValue(),
      "edit"
    );
  }

  /**
   * Resets an editable element position.
   */
  showEditableElement() {
    this.textareaParentStyle.height = "";
    this.textareaParentStyle.overflow = "";
    this.textareaParentStyle.position = "";
    this.textareaParentStyle.right = "auto";
    this.textareaParentStyle.opacity = "1";
    this.textareaParentStyle.width = `${
      this.getEditedCell().getBoundingClientRect().width
    }px`;

    this.textareaStyle.textIndent = "";
    this.textareaStyle.overflowY = "hidden";

    const childNodes = this.TEXTAREA_PARENT.childNodes;
    let hasClassHandsontableEditor = false;

    rangeEach(childNodes.length - 1, (index) => {
      const childNode = childNodes[index];

      if (hasClass(childNode, "handsontableEditor")) {
        hasClassHandsontableEditor = true;

        return false;
      }
    });

    if (hasClass(this.TEXTAREA_PARENT, EDITOR_HIDDEN_CLASS_NAME)) {
      removeClass(this.TEXTAREA_PARENT, EDITOR_HIDDEN_CLASS_NAME);
    }

    if (hasClassHandsontableEditor) {
      this.layerClass = EDITOR_VISIBLE_CLASS_NAME;

      addClass(this.TEXTAREA_PARENT, this.layerClass);
    } else {
      this.layerClass = this.getEditedCellsLayerClass();

      addClass(this.TEXTAREA_PARENT, this.layerClass);
    }
  }

  /**
   * onChoicesSearch callback.
   *
   * @param {Event} event
   */

  async onChoicesSearch(event) {
    if (!event.target.value || !this.queryKey) return;
    clearTimeout(choicesSearchSetTimeout);
    const { choices } = this;

    let variables = this.getVariables();

    let searchValues = [
      {
        name: {
          iLike: `%${event.target.value}%`,
        },
      },
    ];

    if (this.queryKey === "annualSubscriptions") {
      searchValues = [
        { client: { name: { iLike: `%${event.target.value}%` } } },
      ];
    }

    const extraFilters = {
      ...store.getters["misc/findExtraSearchDetailsForDimensionsOnGrid"](
        store.state.misc.currentActiveGridRow,
        this.queryKey
      ),
    };

    const allExtraFilters = [];
    if (Object.keys(extraFilters).length > 0) {
      for (const key in extraFilters) {
        if (!extraFilters[key]) continue;
        allExtraFilters.push({
          [key]: {
            eq: extraFilters[key],
          },
        });
      }
    }

    variables = {
      ...variables,
      filter: {
        ...variables.filter,
        and: [
          ...(variables?.filter?.and ?? []),
          ...searchValues,
          ...allExtraFilters,
        ],
      },
    };

    const { data } = await apolloClient.query({
      query: this.queryDocument,
      fetchPolicy: "cache-first",
      errorPolicy: "all",
      variables,
    });

    const options = data[this.queryKey]
      ? data[this.queryKey].edges.map((option) => {
          return {
            ...option.node,
            [this.valueKey]: option.node.client
              ? option.node.client.id
              : option.node.id,
            [this.labelKey]: option.node.client
              ? option.node.client.name
              : option.node.name,
          };
        })
      : [];
    handleCachedSearchedAndQuickAddOptions(options, this.queryKey);
    choices.setChoices(options, this.valueKey, this.labelKey, true);
    choices.showDropdown();
    setDataReference(this.queryKey, options);
  }

  /**
   * onChoicesChange callback.
   *
   * @param {Event} event
   */
  onChoicesChange(event) {
    const { choices, maxItemCount } = this;
    const selected = choices.getValue();

    if (maxItemCount !== -1 && selected.length >= maxItemCount) {
      this.close();
      this.finishEditing();
    }
  }

  /**
   * onBeforeKeyDownOnInput callback.
   *
   * @param {Event} event
   */

  async onBeforeKeyDownOnInput(event) {
    const { choices } = this;
    try {
      const keyCodes = Handsontable.helper.KEY_CODES;
      if (event.keyCode !== 13) {
        choicesSearchSetTimeout = setTimeout(async () => {
          await this.onChoicesSearch(event);
        }, 500);
      }
      switch (event.keyCode) {
        case KEY_CODES.ARROW_UP:
        case KEY_CODES.ARROW_DOWN:
          event.preventDefault();
          event.stopPropagation();
          break;
        case keyCodes.ENTER:
          if (event.target.value && this.addItem) {
            event.stopImmediatePropagation();
            event.preventDefault();
            event.stopPropagation();
            const id = uuidv4();

            const valueToUse =
              this.queryKey === "groups"
                ? scrubOffUnnecessaryLettersFromText(event.target.value)
                : event.target.value;

            const optionItem = {
              [this.valueKey]: `${valueToUse}_quick_add_${id}`,
              [this.labelKey]: valueToUse,
            };

            const options = [optionItem];
            handleCachedSearchedAndQuickAddOptions(options, this.queryKey);
            choices.setValue([
              {
                value: optionItem[this.valueKey],
                label: valueToUse,
              },
            ]);
            const linterEvent = event;
            linterEvent.target.value = "";
          }
          break;
        default:
          break;
      }
    } catch (error) {
      //
    }
  }

  /**
   * onBeforeKeyDownOnSelect callback.
   *
   * @param {Event} event
   */
  async onBeforeKeyDownOnSelect(event) {
    try {
      const keyCodes = Handsontable.helper.KEY_CODES;
      switch (event.keyCode) {
        case keyCodes.ENTER:
          if (
            this.addItem &&
            this.TEXTAREA_PARENT.querySelector("input").value.length > 31
          ) {
            // for quick add
          }
          break;
        default:
          break;
      }
    } catch (error) {
      //
    }
  }

  /**
   * onBeforeKeyDown callback.
   *
   * @param {Event} event
   */
  onBeforeKeyDown(event) {
    const keyCodes = Handsontable.helper.KEY_CODES;
    const ctrlDown = (event.ctrlKey || event.metaKey) && !event.altKey; // catch CTRL but not right ALT (which in some systems triggers ALT+CTRL)

    // Process only events that have been fired in the editor
    if (event.target.tagName !== "INPUT") {
      return;
    }
    if (
      event.keyCode === 17 ||
      event.keyCode === 224 ||
      event.keyCode === 91 ||
      event.keyCode === 93
    ) {
      // when CTRL or its equivalent is pressed and cell is edited, don't prepare selectable text in textarea
      event.stopImmediatePropagation();
      return;
    }

    const { target } = event;

    switch (event.keyCode) {
      case keyCodes.ARROW_RIGHT:
        if (Handsontable.dom.getCaretPosition(target) !== target.value.length) {
          event.stopImmediatePropagation();
        }
        break;

      case keyCodes.ARROW_LEFT:
        if (Handsontable.dom.getCaretPosition(target) !== 0) {
          event.stopImmediatePropagation();
        }
        break;

      case keyCodes.ENTER:
        event.stopImmediatePropagation();
        event.preventDefault();
        event.stopPropagation();
        break;

      case keyCodes.A:
      case keyCodes.X:
      case keyCodes.C:
      case keyCodes.V:
        if (ctrlDown) {
          event.stopImmediatePropagation(); // CTRL+A, CTRL+C, CTRL+V, CTRL+X should only work locally when cell is edited (not in table context)
        }
        break;

      case keyCodes.BACKSPACE:
        event.stopImmediatePropagation();
        break;
      case keyCodes.DELETE:
      case keyCodes.HOME:
      case keyCodes.END:
        event.stopImmediatePropagation(); // backspace, delete, home, end should only work locally when cell is edited (not in table context)
        break;

      default:
        break;
    }
  }
}

const gridNumberOfMultiselectItemsToShow = 10;

export function MultiSelectRenderer(
  instance,
  td,
  row,
  col,
  prop,
  value,
  cellProperties
) {
  // Use Handsontable's built-in TextRenderer to apply className and validation styling
  // eslint-disable-next-line prefer-rest-params
  Handsontable.renderers.TextRenderer.apply(this, arguments);
  const allValues = value && Array.isArray(value) ? value : [];
  td.innerHTML = "";
  if (allValues.length === 0) return td;
  value = allValues.slice(0, gridNumberOfMultiselectItemsToShow);
  let text = value?.map((v) => v["name"]).join("; ") || "";
  if (allValues.length > gridNumberOfMultiselectItemsToShow) {
    text += `, and ${
      allValues.length - gridNumberOfMultiselectItemsToShow
    } more`;
  }
  td.innerHTML = text;
  return td;
}

export function MultiSelectProperty(attr) {
  return function (row) {
    return row[attr].map((i) => i.name).join("; ");
  };
}
